import { INVOICE_DOCUMENT_TYPE_PREFIX } from "./invoiceNumberHelpers";
import {
  createDefaultLineDiscountFields,
  displayLineItemInputValue,
  formatInvoiceMoney,
  isApplicationFeeDescription,
  isApplicationFeeLine,
  normalizeDecimalInput,
  normalizeInvoiceAmountInput,
  normalizeTaxType,
  resolveSavedLineTaxRate,
  resolveSavedLineTaxType,
  withSyncedLineDiscount,
} from "./invoiceLineItemHelpers";
import {
  buildInvoiceCreatePayload,
  buildInvoiceLineItemsPayload,
} from "./invoiceCreateHelpers";
import { mergeInvoiceDiscountIntoTotals } from "./invoiceDiscountHelpers";
import {
  DEFAULT_INVOICE_STATUS,
  deriveInvoiceStatusFromPayment,
  normalizeInvoiceStatus,
  resolveCreateInvoiceStatusFromPayment,
} from "./invoiceStatusHelpers";
import { mapOverdueToAwaitingPayment } from "./invoiceOverdueHelpers";
import {
  calculateInvoiceLineTotals,
  isCommissionInvoiceLineItems,
  resolveExTaxFooterTotals,
  resolvePayableInvoiceTotal,
} from "./invoiceLineItemHelpers";
import {
  resolveInvoiceDisplayTitle,
  resolveInvoiceDocumentAmountDue,
} from "./invoicePreviewHelpers";
import { formatInvoiceClientDisplayAddress, mergeInvoiceClientProfiles } from "./applicationCostHelpers";
import { readBillToPrintOptionsFromSnapshot } from "../components/reports/commissions/commissionBillToUtils";
import { readInvoiceDisplayOptionsFromSnapshot } from "../components/reports/commissions/commissionInvoiceDisplayOptions";
import { collectTransactionAttachmentUrls } from "./financeInvoicePaymentHelpers";
import { INVOICE_FOLLOWUP_NOTE_ACTIONS } from "./followupConstants";
import { formatInvoiceFollowupActivityForHistory } from "./invoiceFollowupHelpers";

function parseJsonValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function parseJsonArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  const parsed = parseJsonValue(value);
  if (Array.isArray(parsed)) return parsed;
  if (parsed && Array.isArray(parsed.data)) return parsed.data;
  if (parsed && typeof parsed === "object") return [parsed];

  return [];
}

export function resolveInvoiceLineItemsFromRecord(invoice) {
  if (!invoice) return [];

  const candidates = [
    invoice.line_items,
    invoice.lineItems,
    invoice.items,
    invoice.invoice_items,
    invoice.invoice_line_items,
    invoice.finance_invoice_line_items,
    invoice.FinanceInvoiceLineItems,
  ];

  for (const candidate of candidates) {
    const parsed = parseJsonArray(candidate);
    if (parsed.length) return parsed;
  }

  if (invoice.invoice && typeof invoice.invoice === "object") {
    return resolveInvoiceLineItemsFromRecord(invoice.invoice);
  }

  return [];
}

function resolveRowUnitPrice(row, invoice, quantity) {
  const direct =
    row.unit_price ??
    row.unitPrice ??
    row.price ??
    row.Price ??
    row.rate;

  if (direct !== null && direct !== undefined && direct !== "") {
    return normalizeInvoiceAmountInput(direct);
  }

  const net = Number(row.net);
  if (net > 0 && quantity > 0) {
    return normalizeInvoiceAmountInput(net / quantity);
  }

  const amount = Number(row.amount ?? row.line_total ?? row.total);
  if (amount > 0 && quantity > 0) {
    return normalizeInvoiceAmountInput(amount / quantity);
  }

  if (invoice && Number(invoice.subtotal) > 0) {
    return normalizeInvoiceAmountInput(invoice.subtotal);
  }

  if (invoice && Number(invoice.total_amount ?? invoice.totalAmount) > 0) {
    return normalizeInvoiceAmountInput(
      invoice.total_amount ?? invoice.totalAmount,
    );
  }

  return "";
}

function resolveRowTaxRate(row, invoice) {
  const direct = row.tax_rate ?? row.taxRate ?? row.gst_percentage ?? row.gst_rate;
  if (direct !== null && direct !== undefined && direct !== "") {
    return normalizeDecimalInput(direct);
  }

  const invoiceRate =
    invoice?.gst_percentage ??
    invoice?.tax_rate ??
    invoice?.gst_rate;
  if (invoiceRate !== null && invoiceRate !== undefined && invoiceRate !== "") {
    return normalizeDecimalInput(invoiceRate);
  }

  return "";
}

function buildFallbackLineItemsFromInvoice(invoice) {
  if (!invoice) return [];

  const description =
    invoice.description ||
    invoice.invoice_title ||
    invoice.invoiceTitle ||
    "";
  const total =
    Number(invoice.subtotal) ||
    Number(invoice.total_amount ?? invoice.totalAmount) ||
    0;

  if (!description && total <= 0) return [];

  const taxType =
    normalizeTaxType(invoice.tax_type || invoice.gst_status || invoice.taxType) ||
    "notax";

  return [
    {
      description,
      quantity: 1,
      unit_price: total,
      discount: Number(invoice.discount_total ?? invoice.discountTotal) || 0,
      tax_type: taxType,
      tax_rate: resolveRowTaxRate({}, invoice),
      amount: total,
      tax_amount: Number(invoice.tax_amount ?? invoice.taxAmount) || 0,
    },
  ];
}

export function mapSavedLineItemsToForm(lineItems, lineIdRef, invoice = null) {
  const sourceInvoice = invoice || { line_items: lineItems };
  let items = resolveInvoiceLineItemsFromRecord(sourceInvoice);

  if (!items.length) {
    items = buildFallbackLineItemsFromInvoice(sourceInvoice);
  }

  if (!items.length) return [];

  return items.map((row, index) => {
    const id = `line-${index + 1}`;
    lineIdRef.current = Math.max(lineIdRef.current, index + 1);

    const rawQty = Number(row.quantity ?? row.qty ?? row.Qty ?? 1);
    const quantityValue =
      Number.isFinite(rawQty) && rawQty > 0 ? rawQty : 1;
    const quantity =
      normalizeInvoiceAmountInput(quantityValue) ||
      String(quantityValue);
    const invoiceTaxType =
      sourceInvoice?.tax_type ||
      sourceInvoice?.gst_status ||
      sourceInvoice?.taxType;

    const description =
      row.description ||
      row.item_description ||
      row.name ||
      row.title ||
      "";
    const isApplicationFeeLine = isApplicationFeeDescription(description);

    return {
      ...createDefaultLineDiscountFields(),
      id,
      line_item_id: row.id ?? row.line_item_id ?? row.lineItemId ?? null,
      description,
      quantity: String(quantity),
      unit_price: resolveRowUnitPrice(row, sourceInvoice, quantity),
      discount: displayLineItemInputValue(
        row.discount ?? row.disc ?? row.discount_amount ?? row.discountAmount,
      ),
      discount_tab:
        row.discount_tab ||
        (row.discount_rule_id || row.discountRuleId ? "predefined" : "predefined"),
      discount_rule_id: String(
        row.discount_rule_id ?? row.discountRuleId ?? "",
      ),
      discount_custom_amount: displayLineItemInputValue(
        row.discount_custom_amount ?? row.discountCustomAmount ?? "",
      ),
      discount_percent: displayLineItemInputValue(
        row.discount_percent ??
          row.discount_percentage ??
          row.discountPercentage ??
          "",
      ),
      discount_reason: String(
        row.discount_reason ?? row.discountReason ?? "",
      ),
      tax_rate: resolveSavedLineTaxRate(row, sourceInvoice),
      tax_type: resolveSavedLineTaxType(row, invoiceTaxType),
      unit_price_locked: isApplicationFeeLine,
      tax_type_locked: isApplicationFeeLine,
      application_id:
        row.application_id ?? row.applicationId ?? null,
      application_type:
        row.application_type || row.applicationType || "",
      fee_type: row.fee_type || row.feeType || "",
      commission_installment_id:
        row.commission_installment_id ??
        row.commissionInstallmentId ??
        null,
      bonus_installment_id:
        row.bonus_installment_id ?? row.bonusInstallmentId ?? null,
      student_discount:
        row.student_discount ?? row.studentDiscount ?? "",
      discount_paid: row.discount_paid ?? row.discountPaid ?? "",
      discount_balance: row.discount_balance ?? row.discountBalance ?? "",
      balance_fee: row.balance_fee ?? row.balanceFee ?? "",
      net_commission: row.net_commission ?? row.netCommission ?? "",
    };
  });
}

export function resolvePrimaryInvoiceLineItemId(lineItems = [], invoice = null) {
  const firstLine = Array.isArray(lineItems) ? lineItems[0] : null;
  if (firstLine?.line_item_id) return firstLine.line_item_id;

  const savedItems = resolveInvoiceLineItemsFromRecord(invoice);
  if (savedItems[0]?.id) return savedItems[0].id;

  return null;
}

export function toInvoiceDateInputValue(value) {
  if (!value) return "";
  const direct = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (direct) return direct[1];

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function inferDocumentTypeFromInvoice(invoice) {
  const explicit = invoice?.document_type || invoice?.documentType;
  if (explicit) return explicit;

  const invoiceNumber =
    invoice?.invoice_number || invoice?.invoice_no || invoice?.invoiceNo || "";
  const prefix = String(invoiceNumber).split("-")[0]?.toUpperCase();
  const match = Object.entries(INVOICE_DOCUMENT_TYPE_PREFIX).find(
    ([, value]) => value === prefix,
  );
  return match ? match[0] : "invoice";
}

export function mapClientSnapshotToProfile(snapshot) {
  if (!snapshot) return null;

  const billingAddress = String(snapshot.address || "").trim();

  return {
    id: snapshot.id ?? snapshot.client_id ?? null,
    fullName:
      snapshot.full_name ||
      snapshot.fullName ||
      snapshot.client_name ||
      snapshot.clientname ||
      snapshot.name ||
      "",
    email: snapshot.email || snapshot.client_email || snapshot.clientemail || "",
    phone: snapshot.phone || "",
    address: billingAddress,
    country: snapshot.country || "",
    city: snapshot.city || "",
    campus: snapshot.campus || snapshot.city || "",
    description: snapshot.description || "",
  };
}

export function buildClientProfileFromInvoiceRecord(invoice) {
  const snapshotProfile = mapClientSnapshotToProfile(invoice?.client_snapshot);

  const invoiceProfile = {
    id:
      invoice?.client_id ||
      invoice?.clientId ||
      snapshotProfile?.id ||
      null,
    fullName:
      invoice?.client_name ||
      invoice?.clientName ||
      snapshotProfile?.fullName ||
      "",
    email:
      invoice?.client_email ||
      invoice?.clientemail ||
      snapshotProfile?.email ||
      "",
    phone:
      invoice?.client_phone ||
      invoice?.clientPhone ||
      invoice?.client_mobile ||
      invoice?.clientMobile ||
      snapshotProfile?.phone ||
      "",
    country:
      invoice?.client_country ||
      invoice?.clientCountry ||
      snapshotProfile?.country ||
      "",
    city:
      invoice?.client_city ||
      invoice?.clientCity ||
      snapshotProfile?.city ||
      "",
    description: snapshotProfile?.description || "",
    address:
      invoice?.client_address ||
      invoice?.clientAddress ||
      snapshotProfile?.address ||
      "",
  };

  if (!snapshotProfile?.fullName && !snapshotProfile?.id) {
    return {
      ...invoiceProfile,
      address: formatInvoiceClientDisplayAddress(invoiceProfile),
    };
  }

  return mergeInvoiceClientProfiles(snapshotProfile, invoiceProfile);
}

export function normalizeFinanceInvoiceRecord(response) {
  let payload =
    response?.data?.data ??
    response?.data?.invoice ??
    response?.data;

  if (!payload || typeof payload !== "object") return null;
  if (Array.isArray(payload)) payload = payload[0] || null;
  if (!payload) return null;
  if (payload.data && typeof payload.data === "object" && !payload.line_items) {
    payload = payload.data;
  }

  const normalized = { ...payload };

  if (typeof normalized.client_snapshot === "string") {
    normalized.client_snapshot =
      parseJsonValue(normalized.client_snapshot) || normalized.client_snapshot;
  }
  if (typeof normalized.branding_snapshot === "string") {
    normalized.branding_snapshot =
      parseJsonValue(normalized.branding_snapshot) || normalized.branding_snapshot;
  }
  if (typeof normalized.branch_snapshot === "string") {
    normalized.branch_snapshot =
      parseJsonValue(normalized.branch_snapshot) || normalized.branch_snapshot;
  }

  const resolvedLineItems = resolveInvoiceLineItemsFromRecord(normalized);
  if (resolvedLineItems.length) {
    normalized.line_items = resolvedLineItems;
  }

  const resolvedNotes = parseJsonArray(normalized.notes);
  if (resolvedNotes.length) {
    normalized.notes = resolvedNotes;
  }

  const resolvedTransactions = parseJsonArray(normalized.transactions);
  normalized.transactions = resolvedTransactions.length
    ? resolvedTransactions
    : Array.isArray(normalized.transactions)
      ? normalized.transactions
      : [];

  normalized.approved = Boolean(normalized.approved);
  const normalizedStatus = normalizeInvoiceStatus(
    normalized.status || normalized.payment_status || "",
  );
  if (normalizedStatus === "approved") {
    normalized.approved = true;
    normalized.status = "awaiting_payment";
  }

  return ensureFinanceInvoiceApprovedState(
    reconcileFinanceInvoicePaymentStatus(normalized),
  );
}

/**
 * Workflow UI treats `approved` separately from payment status. The API may
 * return `awaiting_payment` without an explicit approved flag after approve.
 */
export function ensureFinanceInvoiceApprovedState(
  invoice,
  { forceApproved = false } = {},
) {
  if (!invoice || typeof invoice !== "object") return invoice;

  const normalizedStatus = normalizeInvoiceStatus(
    invoice.status || invoice.payment_status || "",
  );

  if (["draft", "awaiting_approval"].includes(normalizedStatus)) {
    return {
      ...invoice,
      approved: false,
      status: normalizedStatus,
      payment_status: invoice.payment_status || normalizedStatus,
    };
  }

  const approved =
    forceApproved ||
    Boolean(invoice.approved) ||
    normalizedStatus === "approved";

  if (!approved) return invoice;

  const resolvedStatus =
    normalizedStatus === "approved" || !normalizedStatus
      ? "awaiting_payment"
      : normalizedStatus;

  return {
    ...invoice,
    approved: true,
    status: resolvedStatus,
    payment_status: invoice.payment_status || resolvedStatus,
  };
}

/**
 * Keep UI status in sync with paid/remaining after payment or reverse.
 * Example: paid 5000 via two 2500 payments → reverse one → partial.
 */
export function reconcileFinanceInvoicePaymentStatus(invoice) {
  if (!invoice || typeof invoice !== "object") return invoice;

  const currentStatus = normalizeInvoiceStatus(
    invoice.status || invoice.payment_status,
  );

  // These statuses are workflow-driven, not only payment-balance driven.
  if (
    ["cancelled", "void", "refunded", "draft", "awaiting_approval"].includes(
      currentStatus,
    )
  ) {
    return invoice;
  }

  const total =
    Number(invoice.total_amount ?? invoice.totalAmount) || 0;
  const paid = resolveInvoicePaidAmount(invoice);
  const due = resolveInvoiceDueAmount(invoice, total);
  const nextStatus = deriveInvoiceStatusFromPayment({
    dueAmount: due,
    paidAmount: paid,
    existingStatus: currentStatus,
    dueDate: invoice.due_date ?? invoice.dueDate ?? null,
  });

  if (nextStatus === currentStatus) return invoice;

  return {
    ...invoice,
    status: nextStatus,
    payment_status: nextStatus,
  };
}

/**
 * Single entry for list tabs, counts, and status labels — wraps payment reconciliation.
 * Preserves draft, awaiting approval, void, cancelled, and refunded.
 */
export function resolveFinanceInvoiceEffectiveStatus(
  invoice,
  { mapOverdueTab = false } = {},
) {
  if (!invoice || typeof invoice !== "object") {
    return DEFAULT_INVOICE_STATUS;
  }

  const notes = Array.isArray(invoice.notes) ? invoice.notes : [];
  if (
    notes.some(
      (note) => String(note?.action || "").toLowerCase() === "voided",
    ) ||
    normalizeInvoiceStatus(invoice.status || invoice.payment_status || "") ===
      "void"
  ) {
    return "void";
  }

  const reconciled = reconcileFinanceInvoicePaymentStatus(invoice);
  let status = normalizeInvoiceStatus(
    reconciled.status ||
      reconciled.payment_status ||
      DEFAULT_INVOICE_STATUS,
  );

  if (status === "approved") {
    status = "awaiting_payment";
  }

  const storedStatus = normalizeInvoiceStatus(
    invoice.status || invoice.payment_status || "",
  );
  if (
    invoice.approved === true &&
    (storedStatus === "draft" ||
      storedStatus === "awaiting_approval" ||
      !storedStatus) &&
    status !== "paid" &&
    status !== "partial"
  ) {
    status = "awaiting_payment";
  }

  if (mapOverdueTab) {
    status = mapOverdueToAwaitingPayment(status);
  }

  return status;
}

export function normalizeFinanceInvoiceFromRecord(record) {
  if (!record || typeof record !== "object") return null;
  return normalizeFinanceInvoiceRecord({ data: { data: record } });
}

export function buildInvoiceUpdatePayload({
  existingInvoice,
  status,
  invoiceTitle = "",
  invoiceNumber,
  issueDate,
  dueDate,
  reference,
  documentType = "",
  documentTaxType,
  branchId,
  selectedBranch,
  brandingId,
  selectedBranding,
  application,
  applicationType = "",
  clientProfile,
  billToProfile = null,
  clientId,
  currency = "AUD",
  lineTotals,
  invoiceDiscount = null,
  payment = null,
  payments = null,
  receiptFile = null,
  receiptFiles = null,
  html = "",
  email = "",
  externalReferringAgent = null,
  externalProcessingAgent = null,
  commissionBillToPrintOptions = null,
}) {
  const hasDraftPayments =
    (Array.isArray(payments) && payments.some((item) => Number(item?.amount) > 0)) ||
    Number(payment?.amount) > 0;

  const resolvedReceiptFiles = Array.isArray(receiptFiles)
    ? receiptFiles
    : receiptFile
      ? [receiptFile]
      : Array.isArray(payments)
        ? payments.map((item) => item?.receiptFile || null)
        : null;

  const payload = buildInvoiceCreatePayload({
    status,
    invoiceTitle,
    invoiceNumber,
    issueDate,
    dueDate,
    reference,
    documentType,
    documentTaxType,
    branchId,
    selectedBranch,
    brandingId,
    selectedBranding,
    application,
    applicationType,
    clientProfile,
    billToProfile,
    clientId,
    currency,
    lineTotals,
    invoiceDiscount,
    payment,
    payments,
    receiptFiles: resolvedReceiptFiles,
    html,
    email,
    externalReferringAgent,
    externalProcessingAgent,
    commissionBillToPrintOptions,
  });

  if (hasDraftPayments) {
    const existingPaid = resolveInvoicePaidAmount(existingInvoice);
    const totalAmount = Number(payload.total_amount) || 0;

    return {
      ...payload,
      line_items: buildInvoiceLineItemsPayload(lineTotals?.lineItems),
      // Balances are updated incrementally by applyInvoicePayment on the backend.
      paid_amount: existingPaid,
      amount_due: Math.max(0, Math.round((totalAmount - existingPaid) * 100) / 100),
    };
  }

  const paidAmount = resolveInvoicePaidAmount(existingInvoice);
  const totalAmount = Number(payload.total_amount) || 0;
  const resolvedStatus = resolveCreateInvoiceStatusFromPayment({
    requestedStatus: status,
    totalAmount,
    paidAmount,
    discountAmount: Number(payload.discount_total) || 0,
    grossSubtotal: Number(payload.gross_total) || 0,
  });

  return {
    ...payload,
    status: resolvedStatus,
    payment_status: resolvedStatus,
    line_items: buildInvoiceLineItemsPayload(lineTotals?.lineItems),
    paid_amount: paidAmount,
    amount_due: Math.max(0, Math.round((totalAmount - paidAmount) * 100) / 100),
  };
}

export function sumInvoiceNetPayments(invoice) {
  if (!invoice) return 0;

  const transactions = resolveFinanceInvoiceTransactions(invoice);
  if (!transactions.length) return 0;

  let netPaid = 0;
  transactions.forEach((record) => {
    const type = normalizeInvoiceTransactionType(
      record?.type ?? record?.transaction_type ?? "",
    );
    if (type !== "payment") return;
    if (isLedgerPaymentReversed(record, transactions)) return;
    netPaid += Math.abs(Number(record.amount) || 0);
  });

  return Math.max(0, Math.round(netPaid * 100) / 100);
}

export function resolveInvoicePaidAmount(invoice) {
  const headerPaid = Number(invoice?.paid_amount ?? invoice?.paidAmount) || 0;
  if (headerPaid > 0) return headerPaid;
  return sumInvoiceNetPayments(invoice);
}

function pickPaymentRecordDate(record) {
  return (
    record?.transaction_date ??
    record?.transactionDate ??
    record?.payment_date ??
    record?.paymentDate ??
    record?.paymentdate ??
    record?.created_at ??
    record?.createdAt ??
    null
  );
}

/** Latest non-reversed payment date for list views (partial / paid tabs). */
export function resolveInvoiceLatestPaymentDate(invoice) {
  if (!invoice) return null;

  const transactions = resolveFinanceInvoiceTransactions(invoice);
  let latestDate = null;
  let latestTs = 0;

  transactions.forEach((record) => {
    const type = normalizeInvoiceTransactionType(
      record?.type ?? record?.transaction_type ?? "",
    );
    if (type !== "payment") return;
    if (isLedgerPaymentReversed(record, transactions)) return;

    const dateValue = pickPaymentRecordDate(record);
    if (!dateValue) return;
    const ts = new Date(dateValue).getTime();
    if (Number.isNaN(ts) || ts < latestTs) return;
    latestTs = ts;
    latestDate = dateValue;
  });

  if (latestDate) return latestDate;

  const headerDate =
    invoice.last_payment_date ??
    invoice.lastPaymentDate ??
    invoice.latest_payment_date ??
    invoice.payment_date ??
    invoice.paymentDate ??
    invoice.paid_at ??
    invoice.paidAt ??
    null;

  return headerDate || null;
}

/** Payment date for invoice preview / PDF (ledger + unsaved draft payments). */
export function resolveInvoicePreviewPaymentDate(
  invoice,
  draftPayments = [],
) {
  const ledgerDate = resolveInvoiceLatestPaymentDate(invoice);
  if (ledgerDate) {
    return toInvoiceDateInputValue(ledgerDate) || ledgerDate;
  }

  const payments = Array.isArray(draftPayments) ? draftPayments : [];
  let latestDate = null;
  let latestTs = 0;

  payments.forEach((payment) => {
    if (Number(payment?.amount) <= 0) return;
    const dateValue = pickPaymentRecordDate(payment);
    if (!dateValue) return;
    const ts = new Date(dateValue).getTime();
    if (Number.isNaN(ts) || ts < latestTs) return;
    latestTs = ts;
    latestDate = dateValue;
  });

  if (!latestDate) return "";
  return toInvoiceDateInputValue(latestDate) || latestDate;
}

export function resolveInvoiceDueAmount(invoice, totalAmount = 0) {
  const paid = resolveInvoicePaidAmount(invoice);
  const storedTotal =
    Number(invoice?.total_amount ?? invoice?.totalAmount) || 0;
  const total = Number(totalAmount) || storedTotal;
  const recomputed = Math.max(0, Math.round((total - paid) * 100) / 100);

  if (paid > 0) {
    return recomputed;
  }

  const explicit = invoice?.amount_due ?? invoice?.amountDue;
  if (
    explicit !== null &&
    explicit !== undefined &&
    explicit !== "" &&
    !(Number(totalAmount) > 0 && Math.abs(total - storedTotal) > 0.009)
  ) {
    return Math.max(0, Number(explicit) || 0);
  }

  return recomputed;
}

export function buildInvoiceXeroPreviewDraftFromRecord(
  invoice,
  { invoiceSetting = null, discountFeeRules = [] } = {},
) {
  if (!invoice) return null;

  const lineIdRef = { current: 0 };
  const mappedLines = mapSavedLineItemsToForm(null, lineIdRef, invoice);
  const hydratedLines = hydrateEditLineItemDiscounts(
    mappedLines,
    invoice,
    discountFeeRules,
  );
  const lineTotals = calculateInvoiceLineTotals(hydratedLines);
  const isCommissionInvoice = isCommissionInvoiceLineItems(lineTotals.lineItems);
  const documentType = inferDocumentTypeFromInvoice(invoice);
  const branding = invoice.branding_snapshot || null;
  const branch = invoice.branch_snapshot || null;
  const clientProfile = buildClientProfileFromInvoiceRecord(invoice);

  const documentTaxType =
    normalizeTaxType(
      invoice.tax_type ||
        invoice.gst_status ||
        invoice.taxType ||
        hydratedLines[0]?.tax_type,
    ) || "inclusive";

  const xeroExTaxDisplayTotals = resolveExTaxFooterTotals(
    lineTotals.lineItems,
    documentTaxType,
  );

  const savedDiscountAmount = getInvoiceDiscountAmount(invoice);
  const invoiceDiscountReason = getInvoiceDiscountReason(invoice);
  const lineDiscountTotal = Number(lineTotals.discount_total) || 0;
  const discountForTotals =
    savedDiscountAmount > 0 && lineDiscountTotal <= 0
      ? { amount: savedDiscountAmount }
      : null;
  const mergedTotals = mergeInvoiceDiscountIntoTotals(
    lineTotals,
    discountForTotals,
  );

  let effectiveTotals = mergedTotals;
  if (isCommissionInvoice) {
    effectiveTotals = {
      ...mergedTotals,
      total_amount: xeroExTaxDisplayTotals.total,
      amount_due: xeroExTaxDisplayTotals.total,
      subtotal: lineTotals.subtotal,
      tax_amount: lineTotals.tax_amount,
      inclusive_tax_amount: lineTotals.inclusive_tax_amount,
      exclusive_tax_amount: lineTotals.exclusive_tax_amount,
      discount_total:
        Number(mergedTotals.invoice_discount_amount) ||
        Number(lineTotals.discount_total) ||
        0,
    };
  } else if (xeroExTaxDisplayTotals) {
    effectiveTotals = {
      ...mergedTotals,
      total_amount: xeroExTaxDisplayTotals.total,
      amount_due: xeroExTaxDisplayTotals.total,
      subtotal: xeroExTaxDisplayTotals.subtotalExTax,
      tax_amount: xeroExTaxDisplayTotals.gst,
    };
  }

  const totalAmount = resolvePayableInvoiceTotal({
    effectiveTotals,
    xeroExTaxDisplayTotals,
    lineTotals,
    storedTotal: invoice.total_amount ?? invoice.totalAmount,
  });
  const paidAmount = resolveInvoicePaidAmount(invoice);
  const amountDue = resolveInvoiceDocumentAmountDue({
    displayTotal: totalAmount,
    paidTotal: paidAmount,
    explicitAmountDue: invoice.amount_due ?? invoice.amountDue,
  });
  const discountTotal = isCommissionInvoice
    ? Number(effectiveTotals.discount_total) || 0
    : Number(invoice.discount_total ?? invoice.discountTotal) ||
      Number(effectiveTotals.invoice_discount_amount) ||
      Number(lineTotals.discount_total) ||
      0;

  return {
    documentType,
    brandingTitle:
      invoice.invoice_title ||
      resolveInvoiceDisplayTitle({ branding, documentType }),
    invoiceNumber:
      invoice.invoice_number || invoice.invoice_no || invoice.invoiceNo || "",
    issueDate: toInvoiceDateInputValue(
      invoice.issue_date ||
        invoice.issueDate ||
        invoice.cost_date ||
        invoice.created_at,
    ),
    dueDate: toInvoiceDateInputValue(invoice.due_date || invoice.dueDate),
    reference: invoice.reference || "",
    clientProfile,
    previewDisplay: {
      showLineDiscount: !isCommissionInvoice,
      showDiscountTotal: !isCommissionInvoice,
      useCommissionLineLayout: false,
      useExTaxLineLayout: true,
      invoiceDisplayOptions: readInvoiceDisplayOptionsFromSnapshot(
        invoice?.client_snapshot,
      ),
      ...(isCommissionInvoice
        ? {
            commissionBillToPrintOptions: readBillToPrintOptionsFromSnapshot(
              invoice?.client_snapshot,
            ),
          }
        : {}),
    },
    currency: invoice.currency || "AUD",
    status: invoice.status,
    taxType: documentTaxType,
    branding,
    branch,
    invoiceSetting,
    lineItems: lineTotals.lineItems,
    gross_subtotal:
      Number(invoice.gross_total ?? invoice.grossTotal) ||
      lineTotals.gross_subtotal ||
      0,
    discount_total: discountTotal,
    subtotal:
      xeroExTaxDisplayTotals?.subtotalExTax ?? effectiveTotals.subtotal ?? 0,
    tax_amount: xeroExTaxDisplayTotals?.gst ?? effectiveTotals.tax_amount ?? 0,
    inclusive_tax_amount:
      Number(invoice.inclusive_tax_amount) || lineTotals.inclusive_tax_amount || 0,
    exclusive_tax_amount:
      Number(invoice.exclusive_tax_amount) || lineTotals.exclusive_tax_amount || 0,
    total_amount: totalAmount,
    paid_amount: paidAmount,
    amount_due: amountDue,
    paymentDate: resolveInvoicePreviewPaymentDate(invoice),
    invoice_discount_amount:
      Number(effectiveTotals.invoice_discount_amount) || savedDiscountAmount || 0,
    invoice_discount_description: invoiceDiscountReason,
  };
}

export function getLedgerInvoiceId(record) {
  const value =
    record?.invoice_id ??
    record?.invoiceId ??
    record?.invoiceid ??
    record?.metadata?.invoice_id ??
    record?.metadata?.invoiceId;

  return value !== null && value !== undefined && value !== "" ? value : null;
}

export function getInvoiceDiscountAmount(invoice) {
  return (
    Number(
      invoice?.discount_total ??
        invoice?.discountTotal ??
        invoice?.discount_amount ??
        invoice?.discountAmount,
    ) || 0
  );
}

export function getInvoiceDiscountReason(invoice) {
  return String(
    invoice?.discount_reason ??
      invoice?.discountReason ??
      invoice?.discount_description ??
      invoice?.discountDescription ??
      "",
  ).trim();
}

export function hydrateEditLineItemDiscounts(lines, invoice, rules = []) {
  if (!Array.isArray(lines) || !lines.length) return [];

  const invoiceDiscount = getInvoiceDiscountAmount(invoice);
  const invoiceReason = getInvoiceDiscountReason(invoice);
  const hasAnyLineDiscount = lines.some((row) => Number(row.discount) > 0);

  let hydrated = lines.map((row) => {
    const discountAmount = Number(row.discount) || 0;
    let next = {
      ...createDefaultLineDiscountFields(),
      ...row,
    };

    if (discountAmount > 0) {
      if (!next.discount_rule_id && !next.discount_custom_amount) {
        next.discount_tab = "custom";
        next.discount_custom_amount = String(discountAmount);
      }
    }

    return withSyncedLineDiscount(next, rules);
  });

  if (invoiceDiscount > 0 && !hasAnyLineDiscount) {
    const targetIndex = hydrated.findIndex((row) => isApplicationFeeLine(row));
    const index = targetIndex >= 0 ? targetIndex : 0;

    hydrated = hydrated.map((row, rowIndex) => {
      if (rowIndex !== index) return row;

      const next = {
        ...row,
        discount_tab: "custom",
        discount_custom_amount: String(invoiceDiscount),
        discount_reason: invoiceReason || row.discount_reason,
      };
      return withSyncedLineDiscount(next, rules);
    });
  } else if (invoiceReason) {
    hydrated = hydrated.map((row) => {
      if (Number(row.discount) <= 0 || row.discount_reason) return row;
      return { ...row, discount_reason: invoiceReason };
    });
  }

  return hydrated;
}

export function resolveLineDiscountTransactionId(
  row,
  ledgerEntries = [],
  activeDiscountTransaction = null,
) {
  const lineItemId = row?.line_item_id;
  if (lineItemId && Array.isArray(ledgerEntries)) {
    const match = ledgerEntries.find((entry) => {
      if (String(entry?.type || "").toLowerCase() !== "discount") return false;
      const entryItemId =
        entry.invoice_item_id ??
        entry.invoiceItemId ??
        entry.line_item_id ??
        entry.lineItemId;
      return entryItemId && String(entryItemId) === String(lineItemId);
    });
    if (match?.id) return match.id;
  }

  if (
    activeDiscountTransaction?.id &&
    Number(row?.discount) > 0 &&
    String(row?.id) === "line-1"
  ) {
    return activeDiscountTransaction.id;
  }

  return null;
}

export function getInvoiceDiscountLedgerId(invoice) {
  const value =
    invoice?.discount_ledger_id ??
    invoice?.discountLedgerId ??
    invoice?.ledger_entry_id ??
    invoice?.ledgerEntryId ??
    invoice?.discount?.ledger_id ??
    invoice?.discount?.ledger_entry_id ??
    invoice?.discount?.id ??
    null;

  return value !== null && value !== undefined && value !== "" ? value : null;
}

function normalizeInvoiceTransactionType(type) {
  const normalized = String(type || "").toLowerCase();

  if (normalized === "payment_reversal") return "reversal";
  if (normalized === "discount_reversal") return "discount_reversal";

  return normalized;
}

function getTransactionReversesId(record) {
  if (!record || typeof record !== "object") return null;

  const direct =
    record.reverses_entry_id ??
    record.reverses_transaction_id ??
    record.reversed_transaction_id ??
    record.reversed_payment_id ??
    record.original_transaction_id ??
    record.parent_transaction_id ??
    record.payment_transaction_id ??
    record.related_transaction_id ??
    null;

  if (direct !== null && direct !== undefined && direct !== "") {
    return direct;
  }

  const reference = String(record.reference || "").trim();
  const reversalMatch = reference.match(/^payment_reversal_(\d+)$/i);
  if (reversalMatch) {
    return reversalMatch[1];
  }

  return null;
}

function resolveTransactionRecordedBy(record) {
  if (!record || typeof record !== "object") return null;

  if (record.creator?.name) {
    return record.creator;
  }

  if (record.user?.name) {
    return record.user;
  }

  if (typeof record.user === "object" && record.user) {
    return record.user;
  }

  const createdByName = String(
    record.created_by_name || record.createdByName || "",
  ).trim();

  if (createdByName) {
    return {
      id: record.created_by ?? record.createdBy ?? null,
      name: createdByName,
    };
  }

  if (typeof record.created_by === "object" && record.created_by?.name) {
    return record.created_by;
  }

  if (typeof record.createdBy === "object" && record.createdBy?.name) {
    return record.createdBy;
  }

  return null;
}

function normalizeInvoiceTransactionRecord(record, invoice) {
  if (!record || typeof record !== "object") return null;

  const type = normalizeInvoiceTransactionType(
    record.type ?? record.transaction_type ?? record.action ?? "",
  );

  if (!type) return null;

  const id =
    record.id ??
    record.ledger_id ??
    record.ledger_entry_id ??
    record.transaction_id ??
    null;

  const attachmentUrls = collectTransactionAttachmentUrls(record);

  return {
    ...record,
    id,
    type,
    amount:
      record.amount ??
      record.discount_amount ??
      record.discountAmount ??
      record.total ??
      0,
    description:
      record.description ??
      record.details ??
      record.discount_reason ??
      record.discountReason ??
      "",
    transaction_date:
      record.transaction_date ??
      record.action_date ??
      record.created_at ??
      null,
    payment_method: record.payment_method ?? record.method ?? "",
    reference: record.reference ?? "",
    finance_invoice_item_id: record.finance_invoice_item_id ?? null,
    commission_installment_id: record.commission_installment_id ?? null,
    bonus_installment_id: record.bonus_installment_id ?? null,
    attachment: attachmentUrls[0] || "",
    attachment_url: attachmentUrls[0] || "",
    attachment_urls: attachmentUrls,
    attachments: attachmentUrls,
    receipt_url: attachmentUrls[0] || "",
    reverses_entry_id: getTransactionReversesId(record),
    is_reversed:
      record.is_reversed === true ||
      record.reversed === true ||
      record.status === "reversed" ||
      String(record.description || "").includes("[REVERSED]"),
    invoice_id: getLedgerInvoiceId(record) ?? invoice?.id ?? null,
    user: resolveTransactionRecordedBy(record),
  };
}

function parseInvoiceEmbeddedTransactions(invoice) {
  const sources = [
    invoice?.transactions,
    invoice?.ledger_transactions,
    invoice?.ledger_entries,
    invoice?.invoice_transactions,
  ];
  const merged = [];

  sources.forEach((source) => {
    parseJsonArray(source).forEach((item) => {
      const normalized = normalizeInvoiceTransactionRecord(item, invoice);
      if (normalized) merged.push(normalized);
    });
  });

  return merged;
}

function sortInvoiceLedgerTransactions(records = []) {
  return [...records].sort(
    (left, right) =>
      new Date(right.transaction_date || right.created_at || 0).getTime() -
      new Date(left.transaction_date || left.created_at || 0).getTime(),
  );
}

function mergeTransactionsById(records = []) {
  const map = new Map();

  records.forEach((record) => {
    if (!record) return;

    const key =
      record.id != null
        ? String(record.id)
        : `${record.type}-${record.transaction_date}-${record.amount}`;

    if (!map.has(key)) {
      map.set(key, record);
    }
  });

  return Array.from(map.values());
}

export function resolveInvoiceDiscountLedgerEntry(ledgerEntries = [], invoice) {
  if (!invoice?.id) return null;

  const entries = ledgerEntries || [];
  const ledgerId = getInvoiceDiscountLedgerId(invoice);

  if (ledgerId) {
    const linked = entries.find(
      (entry) => String(entry.id) === String(ledgerId),
    );
    if (linked) return linked;
  }

  const linkedByInvoice = entries.filter((entry) => {
    if (String(entry?.type || "").toLowerCase() !== "discount") return false;
    return String(getLedgerInvoiceId(entry)) === String(invoice.id);
  });

  if (linkedByInvoice.length) return linkedByInvoice[0];

  const discountAmount = getInvoiceDiscountAmount(invoice);
  const discountReason = getInvoiceDiscountReason(invoice);

  if (discountAmount <= 0) return null;

  const candidates = entries.filter(
    (entry) => String(entry?.type || "").toLowerCase() === "discount",
  );

  const amountMatches = candidates.filter((entry) => {
    const amount = Math.abs(Number(entry.amount) || 0);
    return Math.abs(amount - discountAmount) < 0.01;
  });

  if (!amountMatches.length) return null;

  if (discountReason) {
    const reasonMatch = amountMatches.find((entry) =>
      String(entry.description || "")
        .toLowerCase()
        .includes(discountReason.toLowerCase()),
    );
    if (reasonMatch) return reasonMatch;
  }

  return amountMatches[0];
}

export function buildSyntheticInvoiceDiscountTransaction(invoice) {
  const discountAmount = getInvoiceDiscountAmount(invoice);
  const discountReason = getInvoiceDiscountReason(invoice);

  return {
    id: `invoice-discount-${invoice.id}`,
    type: "discount",
    amount: discountAmount,
    description: discountReason || "Invoice discount",
    transaction_date:
      invoice.issue_date ??
      invoice.issueDate ??
      invoice.created_at ??
      invoice.createdAt ??
      null,
    invoice_id: invoice.id,
    user:
      invoice.created_by_user ??
      invoice.createdByUser ??
      invoice.user ??
      null,
    _synthetic: true,
  };
}

export function canReverseInvoiceLedgerTransaction(
  record,
  relatedEntries = [],
) {
  if (!record?.id) return false;
  if (record._synthetic || record.is_synthetic) return false;
  if (String(record.id).startsWith("invoice-discount-")) return false;

  const type = String(record?.type || "").toLowerCase();
  if (type !== "payment") return false;
  if (isLedgerPaymentReversed(record, relatedEntries)) return false;

  return true;
}

export function buildInvoiceTransactionsList(ledgerEntries = [], invoice = null) {
  if (!invoice?.id) return [];

  const entries = ledgerEntries || [];
  const allowedTypes = new Set([
    "payment",
    "reversal",
    "discount",
    "discount_reversal",
  ]);

  const fromEmbedded = parseInvoiceEmbeddedTransactions(invoice).filter((record) =>
    allowedTypes.has(String(record.type || "").toLowerCase()),
  );
  const fromLedger = filterInvoiceLedgerTransactionsByTypes(entries, invoice, [
    ...allowedTypes,
  ]);

  let merged = mergeTransactionsById([...fromLedger, ...fromEmbedded]);

  const hasDiscount = merged.some(
    (record) => String(record?.type || "").toLowerCase() === "discount",
  );
  const discountAmount = getInvoiceDiscountAmount(invoice);

  if (!hasDiscount && discountAmount > 0) {
    const resolvedDiscount = resolveInvoiceDiscountLedgerEntry(entries, invoice);

    if (resolvedDiscount) {
      merged = mergeTransactionsById([...merged, resolvedDiscount]);

      const reversals = entries.filter((entry) => {
        if (String(entry?.type || "").toLowerCase() !== "discount_reversal") {
          return false;
        }
        return (
          String(entry.reverses_entry_id) === String(resolvedDiscount.id)
        );
      });

      merged = mergeTransactionsById([...merged, ...reversals]);
    } else {
      merged = mergeTransactionsById([
        ...merged,
        buildSyntheticInvoiceDiscountTransaction(invoice),
      ]);
    }
  }

  return sortInvoiceLedgerTransactions(merged);
}

export function getLedgerInvoiceNumber(
  record,
  invoices = [],
  ledgerEntries = [],
) {
  const direct =
    record?.invoice_number ??
    record?.invoice_no ??
    record?.invoiceNumber ??
    record?.metadata?.invoice_number ??
    record?.metadata?.invoice_no;

  if (direct) return String(direct);

  const invoiceId = getLedgerInvoiceId(record);
  if (invoiceId && Array.isArray(invoices) && invoices.length > 0) {
    const match = invoices.find(
      (invoice) => String(invoice.id) === String(invoiceId),
    );
    if (match) {
      return match.invoice_number || match.invoice_no || match.invoiceNo || null;
    }
  }

  if (record?.reverses_entry_id && Array.isArray(ledgerEntries)) {
    const reversedEntry = ledgerEntries.find(
      (entry) => String(entry.id) === String(record.reverses_entry_id),
    );

    if (reversedEntry) {
      return getLedgerInvoiceNumber(reversedEntry, invoices, ledgerEntries);
    }
  }

  return null;
}

export function filterInvoicePaymentTransactions(ledgerEntries, invoice) {
  return filterInvoiceLedgerTransactionsByTypes(ledgerEntries, invoice, [
    "payment",
    "reversal",
  ]);
}

export function filterInvoiceDiscountTransactions(ledgerEntries, invoice) {
  return filterInvoiceLedgerTransactionsByTypes(ledgerEntries, invoice, [
    "discount",
    "discount_reversal",
  ]);
}

export function filterInvoiceLedgerTransactions(ledgerEntries, invoice) {
  return buildInvoiceTransactionsList(ledgerEntries, invoice);
}

export function resolveFinanceInvoiceTransactions(invoice) {
  if (!invoice) return [];

  const items = parseJsonArray(invoice.transactions);
  const normalized = items
    .map((item) => normalizeInvoiceTransactionRecord(item, invoice))
    .filter(Boolean);

  return sortInvoiceLedgerTransactions(mergeTransactionsById(normalized));
}

function extractFinanceInvoiceApiPayload(response) {
  return response?.data?.data ?? response?.data ?? null;
}

function extractFinanceInvoiceTransactionsFromPayload(payload) {
  if (!payload || typeof payload !== "object") return [];

  const sources = [
    payload.transaction,
    payload.transactions,
    payload.finance_invoice_transaction,
    payload.finance_invoice_transactions,
    payload.reversal_transaction,
    payload.payment_transaction,
  ];

  const merged = [];
  sources.forEach((source) => {
    parseJsonArray(source).forEach((item) => merged.push(item));
  });

  return merged;
}

function extractInvoiceNotesFromPayload(payload) {
  if (!payload || typeof payload !== "object") return [];

  const sources = [payload.note, payload.notes, payload.invoice_note];

  const merged = [];
  sources.forEach((source) => {
    parseJsonArray(source).forEach((item) => merged.push(item));
  });

  return merged;
}

export function applyFinanceInvoiceMutationFromResponse(invoice, response) {
  const payload = extractFinanceInvoiceApiPayload(response);
  if (!invoice || !payload || typeof payload !== "object") return invoice;

  let next = {
    ...invoice,
    ...(payload.invoice && typeof payload.invoice === "object" ? payload.invoice : {}),
  };

  const incomingTransactions = extractFinanceInvoiceTransactionsFromPayload(payload);
  if (incomingTransactions.length) {
    const currentTransactions = parseJsonArray(next.transactions);
    const normalizedIncoming = incomingTransactions
      .map((item) => normalizeInvoiceTransactionRecord(item, next))
      .filter(Boolean);

    next.transactions = mergeTransactionsById([
      ...normalizedIncoming,
      ...currentTransactions.map((item) =>
        normalizeInvoiceTransactionRecord(item, next),
      ).filter(Boolean),
    ]);
  }

  const scalarFields = [
    "paid_amount",
    "paidAmount",
    "amount_due",
    "amountDue",
    "discount_total",
    "discountTotal",
    "total_amount",
    "totalAmount",
    "status",
    "payment_status",
    "approved",
  ];

  scalarFields.forEach((field) => {
    if (payload[field] !== undefined && payload[field] !== null) {
      next[field] = payload[field];
    }
  });

  return reconcileFinanceInvoicePaymentStatus(next);
}

export function applyInvoiceNotesMutationFromResponse(notes = [], response) {
  const payload = extractFinanceInvoiceApiPayload(response);
  if (!payload || typeof payload !== "object") return notes;

  const incomingNotes = extractInvoiceNotesFromPayload(payload);
  if (!incomingNotes.length) return notes;

  const merged = [...notes];
  incomingNotes.forEach((item) => {
    const noteId = item?.id;
    if (noteId != null && merged.some((note) => String(note.id) === String(noteId))) {
      return;
    }
    merged.unshift(item);
  });

  return merged;
}

function isLedgerEntryForInvoice(record, invoiceId, ledgerEntries = []) {
  if (!record || !invoiceId) return false;

  const recordInvoiceId = getLedgerInvoiceId(record);
  if (
    recordInvoiceId &&
    String(recordInvoiceId) === String(invoiceId)
  ) {
    return true;
  }

  if (record.reverses_entry_id) {
    const reversedEntry = (ledgerEntries || []).find(
      (entry) => String(entry.id) === String(record.reverses_entry_id),
    );
    if (!reversedEntry) return false;

    const reversedInvoiceId = getLedgerInvoiceId(reversedEntry);
    return (
      reversedInvoiceId &&
      String(reversedInvoiceId) === String(invoiceId)
    );
  }

  return false;
}

export function filterInvoiceLedgerTransactionsByTypes(
  ledgerEntries,
  invoice,
  types = [],
) {
  const invoiceId = invoice?.id;
  if (!invoiceId) return [];

  const allowedTypes = new Set(
    (types || []).map((value) => String(value).toLowerCase()),
  );
  const entries = ledgerEntries || [];

  return entries
    .filter((record) => {
      const type = String(record?.type || "").toLowerCase();
      if (!allowedTypes.has(type)) return false;
      return isLedgerEntryForInvoice(record, invoiceId, entries);
    })
    .sort(
      (left, right) =>
        new Date(right.transaction_date || right.created_at || 0).getTime() -
        new Date(left.transaction_date || left.created_at || 0).getTime(),
    );
}

export function isLedgerPaymentReversed(record, ledgerEntries = []) {
  if (!record?.id) return false;

  if (
    record.is_reversed === true ||
    record.reversed === true ||
    record.status === "reversed"
  ) {
    return true;
  }

  const entries = ledgerEntries || [];
  const paymentAmount = Math.abs(Number(record.amount) || 0);
  let reversedAmount = 0;
  let hasReversalLink = false;

  entries.forEach((entry) => {
    const reversesId = getTransactionReversesId(entry);
    if (String(reversesId) !== String(record.id)) return;

    hasReversalLink = true;

    const entryType = normalizeInvoiceTransactionType(
      entry?.type ?? entry?.transaction_type ?? "",
    );

    if (entryType === "reversal" || entryType === "reverse_payment") {
      reversedAmount += Math.abs(Number(entry.amount) || 0);
    }
  });

  if (!hasReversalLink) return false;
  if (paymentAmount <= 0) return true;

  return reversedAmount >= paymentAmount - 0.01;
}

export function isLedgerDiscountReversed(record, ledgerEntries = []) {
  return isLedgerPaymentReversed(record, ledgerEntries);
}

export function getActiveInvoiceDiscountTransaction(ledgerEntries = [], invoice) {
  if (!invoice?.id) return null;

  const entries = ledgerEntries || [];

  return (
    entries.find((record) => {
      if (String(record?.type || "").toLowerCase() !== "discount") return false;
      if (!isLedgerEntryForInvoice(record, invoice.id, entries)) return false;
      return !isLedgerDiscountReversed(record, entries);
    }) || null
  );
}

export function hasActiveInvoiceDiscountTransaction(ledgerEntries = [], invoice) {
  return Boolean(getActiveInvoiceDiscountTransaction(ledgerEntries, invoice));
}

export function resolveTransactionAttachmentUrl(record) {
  const urls = resolveTransactionAttachmentUrls(record);
  return urls[0] || null;
}

export function resolveTransactionAttachmentUrls(record) {
  return collectTransactionAttachmentUrls(record);
}

export function formatInvoicePaymentLedgerAmount(currency, amount) {
  return formatInvoiceLedgerTransactionAmount(currency, amount, "payment");
}

export function formatInvoiceLedgerTransactionAmount(currency, amount, type) {
  const value = formatInvoiceMoney(Math.abs(parseFloat(amount) || 0), "", {});
  const normalized = String(type || "").toLowerCase();

  if (normalized === "payment" || normalized === "discount") {
    return `${currency} -${value}`;
  }

  if (
    normalized === "reversal" ||
    normalized === "discount_reversal" ||
    normalized === "reverse_payment"
  ) {
    return `${currency} +${value}`;
  }

  return `${currency} ${value}`;
}

export function getInvoiceLedgerTypeLabel(type) {
  const normalized = String(type || "").toLowerCase();

  if (normalized === "payment") return "payment";
  if (normalized === "reverse_payment") return "reverse_payment";
  if (normalized === "reversal") return "reversal";
  if (normalized === "discount") return "discount";
  if (normalized === "discount_reversal") return "discount reversal";

  return normalized || "—";
}

export function isInvoiceNotesApiSuccess(response) {
  return response?.data?.success === true;
}

export function normalizeInvoiceNotesList(response) {
  const data = response?.data?.data;
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.notes)) return data.notes;
  return [];
}

function resolveInvoiceNoteDate(item = {}) {
  const candidates = [
    item.action_date,
    item.actionDate,
    item.created_at,
    item.createdAt,
    item.updated_at,
    item.updatedAt,
    item.date,
    item.timestamp,
  ];

  for (const value of candidates) {
    if (value === null || value === undefined || value === "") continue;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }

  return "";
}

export function mapInvoiceNotesToHistoryEntries(notes = [], options = {}) {
  const { excludeFollowupAudit = false } = options;

  return (notes || [])
    .filter((item) => {
      if (!excludeFollowupAudit) return true;
      const action = String(item?.action || "").toLowerCase();
      return !INVOICE_FOLLOWUP_NOTE_ACTIONS.has(action);
    })
    .map((item, index) => {
      const action = String(item?.action || "note").toLowerCase();
      const isFollowupActivity = INVOICE_FOLLOWUP_NOTE_ACTIONS.has(action);
      const rawDetail =
        item.details || item.detail || item.description || "—";
      const followupFormatted = isFollowupActivity
        ? formatInvoiceFollowupActivityForHistory(rawDetail)
        : null;

      return {
        id: item.id || `invoice-note-${index}`,
        date: resolveInvoiceNoteDate(item),
        user:
          item.user_name ||
          resolveHistoryUserName(item.user) ||
          resolveHistoryUserName(item.created_by) ||
          "—",
        action: item.action || "note",
        detail: followupFormatted
          ? followupFormatted.description
          : rawDetail,
        followupDate: followupFormatted?.followupDate || null,
        followupAssignee: followupFormatted?.assigneeName || null,
        isFollowupActivity,
      };
    })
    .sort(
      (left, right) =>
        new Date(right.date).getTime() - new Date(left.date).getTime(),
    );
}

const FINANCE_NOTE_TYPE_LABELS = {
  1: "WhatsApp",
  2: "Call",
  3: "Skype",
  4: "Email sent",
  5: "Email received",
  6: "In-person",
};

function stripHtmlToPlainText(value) {
  if (value == null || value === "") return "—";
  const text = String(value)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\s+\n/g, "\n")
    .replace(/\n\s+/g, "\n")
    .trim();
  return text || "—";
}

export function extractFinanceNotesList(payload) {
  const root = payload?.data?.data ?? payload?.data ?? payload;
  if (Array.isArray(root?.notes?.data)) return root.notes.data;
  if (Array.isArray(root?.notes)) return root.notes;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root)) return root;
  return [];
}

export function extractClientNotesList(payload) {
  return extractFinanceNotesList(payload);
}

function clientNoteHistoryMeta(item, index, { isFinance = false } = {}) {
  const noteId = item?.id ?? item?.noteid ?? item?.note_id ?? null;
  return {
    id: noteId || `${isFinance ? "finance" : "client"}-note-${index}`,
    noteId,
    shownToClient:
      item?.client_note === 1 || item?.client_note === "1",
    isFinance: Boolean(isFinance),
    noteType:
      item?.noteType != null && item?.noteType !== ""
        ? Number(item.noteType)
        : null,
  };
}

export function mapClientNotesToHistoryEntries(notes = []) {
  return (notes || [])
    .map((item, index) => ({
      ...clientNoteHistoryMeta(item, index, { isFinance: false }),
      date:
        item.created_at ||
        item.action_date ||
        item.updated_at ||
        new Date().toISOString(),
      user:
        item.username ||
        item.user_name ||
        resolveHistoryUserName(item.user) ||
        "System",
      action:
        FINANCE_NOTE_TYPE_LABELS[Number(item.noteType)] ||
        item.action ||
        "General",
      detail: stripHtmlToPlainText(
        item.notes || item.details || item.detail || item.description || "",
      ),
    }))
    .sort(
      (left, right) =>
        new Date(right.date).getTime() - new Date(left.date).getTime(),
    );
}

export function mapFinanceNotesToHistoryEntries(notes = []) {
  return (notes || [])
    .map((item, index) => ({
      ...clientNoteHistoryMeta(item, index, { isFinance: true }),
      date:
        item.created_at ||
        item.action_date ||
        item.updated_at ||
        new Date().toISOString(),
      user:
        item.username ||
        item.user_name ||
        resolveHistoryUserName(item.user) ||
        "System",
      action:
        FINANCE_NOTE_TYPE_LABELS[Number(item.noteType)] ||
        item.action ||
        "Finance",
      detail: stripHtmlToPlainText(
        item.notes || item.details || item.detail || item.description || "",
      ),
    }))
    .sort(
      (left, right) =>
        new Date(right.date).getTime() - new Date(left.date).getTime(),
    );
}

function extractTransactionsFromCommissionInvoiceRow(row) {
  if (!row || typeof row !== "object") return [];

  const candidates = [
    row.transactions,
    row.raw?.transactions,
    row.wrapper?.transactions,
    row.wrapper?.invoice?.transactions,
  ];

  for (const source of candidates) {
    if (Array.isArray(source) && source.length) return source;
  }

  return [];
}

export function collectInvoicePaymentDescriptions(invoiceRows = []) {
  const collected = [];

  (Array.isArray(invoiceRows) ? invoiceRows : []).forEach((row) => {
    const invNumber = row?.invoiceNumber || row?.invoice_number || null;
    const invId = row?.id || row?.financeInvoiceId || null;
    const currency = row?.currency || row?.raw?.currency || "AUD";
    const transactions = resolveFinanceInvoiceTransactions({
      id: invId,
      invoice_number: invNumber,
      transactions: extractTransactionsFromCommissionInvoiceRow(row),
    });

    transactions.forEach((payment) => {
      const type = String(payment?.type || payment?.transaction_type || "")
        .toLowerCase()
        .trim();
      if (type !== "payment") return;

      collected.push({
        ...payment,
        invoice_id: invId,
        invoice_number: invNumber,
        currency,
      });
    });
  });

  collected.sort((left, right) => {
    const leftTime = new Date(
      left.transaction_date || left.created_at || 0,
    ).getTime();
    const rightTime = new Date(
      right.transaction_date || right.created_at || 0,
    ).getTime();
    return rightTime - leftTime;
  });

  return collected;
}

function formatPaymentDescriptionAmount(amount, currency = "AUD") {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0) return "";
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: currency || "AUD",
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return String(value);
  }
}

export function mapPaymentDescriptionsToHistoryEntries(payments = []) {
  return (payments || [])
    .map((item, index) => {
      const description =
        String(item.description || item.details || "").trim() ||
        "Payment received";
      const amountLabel = formatPaymentDescriptionAmount(
        item.amount,
        item.currency,
      );
      const invoiceNo = item.invoice_number || item.invoiceNumber || "";
      const metaParts = [invoiceNo ? `Invoice ${invoiceNo}` : "", amountLabel]
        .filter(Boolean)
        .join(" · ");

      return {
        id: item.id || `payment-description-${index}`,
        date:
          item.transaction_date ||
          item.action_date ||
          item.created_at ||
          new Date().toISOString(),
        user:
          item.user_name ||
          resolveHistoryUserName(item.user) ||
          resolveHistoryUserName(item.created_by) ||
          "—",
        action: "Payment received",
        detail: metaParts ? `${description} (${metaParts})` : description,
      };
    })
    .sort(
      (left, right) =>
        new Date(right.date).getTime() - new Date(left.date).getTime(),
    );
}

function resolveHistoryUserName(source) {
  if (!source) return "—";
  if (typeof source === "string") return source;
  return source.name || source.full_name || source.email || "—";
}

function resolveInvoiceClientName(invoice) {
  return (
    invoice?.client_snapshot?.full_name ||
    invoice?.client_snapshot?.name ||
    invoice?.client_name ||
    invoice?.clientName ||
    "the client"
  );
}

export function buildInvoiceActivityHistory(
  invoice,
  paymentTransactions = [],
  extraNotes = [],
) {
  if (!invoice) return [];

  const entries = [];
  const invoiceNotes = parseJsonArray(invoice.notes);
  const hasApiNotes = invoiceNotes.length > 0;
  const invoiceNo =
    invoice.invoice_number || invoice.invoice_no || invoice.invoiceNo || "";
  const clientName = resolveInvoiceClientName(invoice);
  const totalAmount = Number(
    invoice.total_amount ?? invoice.totalAmount ?? 0,
  ).toFixed(2);

  if (hasApiNotes) {
    invoiceNotes.forEach((item, index) => {
      entries.push({
        id: item.id || `invoice-note-${index}`,
        date: resolveInvoiceNoteDate(item),
        user:
          resolveHistoryUserName(item.user) ||
          resolveHistoryUserName(item.created_by) ||
          item.user_name ||
          "—",
        action: item.action || "Activity",
        detail: item.details || item.detail || item.description || "—",
      });
    });
  }

  if (!hasApiNotes) {
    const createdAt =
      invoice.created_at || invoice.createdAt || invoice.issue_date;
    if (createdAt) {
      entries.push({
        id: `created-${invoice.id}`,
        date: createdAt,
        user:
          resolveHistoryUserName(invoice.created_by) ||
          resolveHistoryUserName(invoice.user) ||
          "System Generated",
        action: "Created",
        detail: `Invoice ${invoiceNo} created for ${clientName} for ${totalAmount}.`,
      });
    }

    const updatedAt = invoice.updated_at || invoice.updatedAt;
    if (
      updatedAt &&
      createdAt &&
      String(updatedAt) !== String(createdAt)
    ) {
      entries.push({
        id: `edited-${invoice.id}-${updatedAt}`,
        date: updatedAt,
        user:
          resolveHistoryUserName(invoice.updated_by) ||
          resolveHistoryUserName(invoice.user) ||
          "System",
        action: "Edited",
        detail: `Invoice ${invoiceNo} updated.`,
      });
    }

    if (invoice.sent_at || invoice.email_sent_at) {
      const sentAt = invoice.sent_at || invoice.email_sent_at;
      const recipient =
        invoice.sent_to ||
        invoice.email_to ||
        invoice.client_snapshot?.email ||
        "";
      entries.push({
        id: `sent-${invoice.id}`,
        date: sentAt,
        user: "System Generated",
        action: "Invoice Sent",
        detail: recipient
          ? `This invoice has been sent to ${recipient}.`
          : `This invoice has been sent.`,
      });
    }

    paymentTransactions.forEach((payment) => {
      const type = String(payment?.type || "").toLowerCase();
      const isReversal = type === "reversal";

      entries.push({
        id: `${type || "payment"}-${payment.id}`,
        date: payment.transaction_date || payment.created_at,
        user: resolveHistoryUserName(payment.user),
        action: isReversal ? "Payment reversed" : "Payment received",
        detail:
          payment.description ||
          (isReversal
            ? `Payment reversed for invoice ${invoiceNo || payment.id}.`
            : `Payment received for invoice ${invoiceNo || payment.id}.`),
      });
    });

    const apiHistory = parseJsonArray(
      invoice.history || invoice.activity_log || invoice.audit_log,
    );
    apiHistory.forEach((item, index) => {
      entries.push({
        id: item.id || `api-history-${index}`,
        date:
          item.date ||
          item.created_at ||
          item.timestamp ||
          item.updated_at ||
          createdAt,
        user:
          resolveHistoryUserName(item.user) ||
          resolveHistoryUserName(item.created_by) ||
          item.user_name ||
          "—",
        action: item.action || item.type || item.event || "Activity",
        detail:
          item.detail ||
          item.description ||
          item.message ||
          item.note ||
          "—",
      });
    });
  }

  extraNotes.forEach((note, index) => {
    entries.push({
      id: note.id || `note-${index}`,
      date: note.created_at || note.date || new Date().toISOString(),
      user: note.user || "—",
      action: "Note",
      detail: note.text || note.detail || note.note || "—",
    });
  });

  return entries.sort(
    (left, right) => new Date(right.date).getTime() - new Date(left.date).getTime(),
  );
}
